import * as React from "react";
import { Grid } from "@mui/material";
import { useParams, useHistory } from "react-router-dom";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import PendingActionWidget from "./PendingActionWidget";
import SearchToolsWidget from "./SearchToolsWidget";
import SearchResultsWidget from "./SearchResultsWidget";
import ChooseProgramModal from "./ChooseProgramModal";
import { useFlash } from "../FlashContext";

const ProgramTransfer: React.FC = (props: any) => {
  const { programId } = useParams();
  const history = useHistory();

  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [program, setProgram] = React.useState(null);
  const [programs, setPrograms] = React.useState<any[]>([]);
  const [selectedProgram, setSelectedProgram] = React.useState(null);
  const [showTransferToProgramModal, setShowTransferToProgramModal] =
    React.useState<boolean>(false);
  const [selectedPatients, setSelectedPatients] = React.useState<any[]>([]);

  const [paramsPresent, setParamsPresent] = React.useState<boolean>(true);
  const [searchPatients, setSearchPatients] = React.useState<any>([]);
  const [pendingAdd, setPendingAdd] = React.useState<any>([]);
  const [pendingRemoval, setPendingRemoval] = React.useState<any>([]);
  const [onList, setOnList] = React.useState<any>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const initialCheckedList = React.useRef<any[]>([]);

  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [sortObject, setSortObject] = React.useState<any>({
    field: "last_name",
    direction: "asc",
  });

  const openTransfer = async () => {
    setShowTransferToProgramModal(true);
  };

  React.useEffect(() => {
    fetchPrograms();
  }, [programId]);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`/programs/published_programs`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const result = await response.json();
      const list = result?.data?.programs || [];

      const available = list.filter((p) => p.id !== Number(programId));
      setPrograms(available);

      const current = list.find((p) => p.id === Number(programId)) || null;
      setProgram(current);
    } catch (err) {
      console.error("Error fetching programs", err);
      alert("Error fetching programs");
    }
  };

  const handleConfirmTransfer = async () => {
    if (!selectedProgram?.id || selectedPatients.length === 0) {
      showToast({
        title: "Validation Error",
        text: "Please select a program to transfer patients",
        type: "error",
      });
      return;
    }

    try {
      const response = await fetch(`/programs/${programId}/transfer_patients`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          to_program_id: selectedProgram?.id,
          patient_ids: selectedPatients.map((p) => p.id),
        }),
      });

      const result = await response.json();
      if (result?.success) {
        setShowTransferToProgramModal(false);
        setSelectedProgram(null);
        setSelectedPatients([]);
        setPendingAdd([]);
        setPendingRemoval([]);
        setOnList([]);
        fetchPrograms();

        history.push("/program-management");

        showToast({
          title: "Patients Transferred",
          text: "Patients transferred successfully",
          type: "success",
        });
      } else {
        showToast({
          title: "Transfer Failed",
          text: "Failed to transfer patients",
          type: "error",
        });
      }
    } catch (e) {
      showToast({
        title: "Transfer Failed",
        text: `Transfer failed: ${e.message}`,
        type: "error",
      });
      console.error("Transfer failed:", e);
    }
  };

  const setInitialCheckedList = (patients: any[]) => {
    initialCheckedList.current = patients;
    setSelectedPatients(patients);
  };

  return (
    <div className="patient-list-create-container">
      <div className="container2">
        <Grid item xs={12}>
          <Grid container>
            <Grid
              container
              justifyContent="space-between"
              className="floating-header"
            >
              <Grid item xs={6} className="list-create-header">
                <span
                  style={{
                    fontSize: "24px",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "normal",
                    color: "black",
                  }}
                >
                  {`${program?.title} - Transfer`}
                </span>
              </Grid>
              <PendingActionWidget
                selectedPatients={selectedPatients}
                pendingAdd={pendingAdd}
                pendingRemoval={pendingRemoval}
                onList={onList}
                handleCreate={openTransfer}
                mode="transfer"
              />
            </Grid>
            <div className="divider"></div>
            <Grid item xs={3}>
              <SearchToolsWidget
                csrfToken={props.csrfToken}
                fixedProgramId={Number(programId)}
                setSearchPatients={setSearchPatients}
                setSelectedPatients={setInitialCheckedList}
                setOnList={setOnList}
                pendingAdd={pendingAdd}
                selectedPatients={selectedPatients}
                setParamsPresent={setParamsPresent}
                setLoading={setLoading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                setTotalPages={setTotalPages}
                sortObject={sortObject}
              />
            </Grid>
            <Grid item xs={9}>
              <SearchResultsWidget
                searchPatients={searchPatients}
                setSearchPatients={setSearchPatients}
                selectedPatients={selectedPatients}
                setSelectedPatients={setSelectedPatients}
                setOnList={setOnList}
                setPendingAdd={setPendingAdd}
                setPendingRemoval={setPendingRemoval}
                onList={onList}
                pendingAdd={pendingAdd}
                pendingRemoval={pendingRemoval}
                paramsPresent={paramsPresent}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                sortObject={sortObject}
                setSortObject={setSortObject}
              />
            </Grid>
          </Grid>
        </Grid>
      </div>

      {showTransferToProgramModal && (
        <ChooseProgramModal
          patientsCount={selectedPatients?.length.toString()}
          programs={programs}
          currentProgram={program}
          selectedProgram={selectedProgram}
          setSelectedProgram={setSelectedProgram}
          showTransferToProgramModal={showTransferToProgramModal}
          setShowTransferToProgramModal={setShowTransferToProgramModal}
          handleConfirmTransfer={handleConfirmTransfer}
        />
      )}
    </div>
  );
};

export default ProgramTransfer;
